import { useState, useEffect, useRef, useMemo } from 'react';
import { getGPUTier } from 'detect-gpu';
import { logger } from '../utils/logger';

// Global cache to prevent multiple GPU detections
let gpuDetectionCache = null;
let gpuDetectionPromise = null;

export const useGPUTier = () => {
  const [gpuTier, setGpuTier] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasDetectedRef = useRef(false);

  useEffect(() => {
    // Prevent multiple detections from the same hook instance
    if (hasDetectedRef.current) {
      return;
    }

    const detectGPUCapabilities = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Use cached result if available
        if (gpuDetectionCache) {
          setGpuTier(gpuDetectionCache);
          setIsLoading(false);
          hasDetectedRef.current = true;
          return;
        }

        // Use existing promise if detection is already in progress
        if (gpuDetectionPromise) {
          const gpuInfo = await gpuDetectionPromise;
          setGpuTier(gpuInfo);
          setIsLoading(false);
          hasDetectedRef.current = true;
          return;
        }

        // Start new detection
        gpuDetectionPromise = getGPUTier();
        const gpuInfo = await gpuDetectionPromise;
        
        // Cache the result
        gpuDetectionCache = gpuInfo;
        
        logger.gpu('GPU Detection Results:', {
          tier: gpuInfo.tier,
          type: gpuInfo.type,
          gpu: gpuInfo.gpu,
          isMobile: gpuInfo.isMobile,
          fps: gpuInfo.fps
        });

        setGpuTier(gpuInfo);
        hasDetectedRef.current = true;
      } catch (err) {
        console.warn('GPU detection failed, using fallback settings:', err);
        setError(err);
        
        // Fallback to conservative settings
        const fallbackGpuInfo = {
          tier: 0, // Conservative tier
          type: 'UNKNOWN',
          gpu: 'Unknown',
          isMobile: /Mobi|Android/i.test(navigator.userAgent),
          fps: 30
        };
        
        gpuDetectionCache = fallbackGpuInfo;
        setGpuTier(fallbackGpuInfo);
        hasDetectedRef.current = true;
      } finally {
        setIsLoading(false);
        gpuDetectionPromise = null;
      }
    };

    detectGPUCapabilities();
  }, []);

  // Performance settings based on GPU tier - memoized to prevent recreation
  const performanceSettings = useMemo(() => {
    if (!gpuTier) {
      return {
        // Conservative fallback settings
        shadowMapSize: 1024,
        maxFPS: 30,
        enableShadows: false,
        enableAntialias: false,
        textureQuality: 'low',
        animationQuality: 'low',
        staticFPS: 10
      };
    }

    const { tier, isMobile } = gpuTier;

    // Tier 0-1: Low-end devices
    if (tier <= 1 || isMobile) {
      return {
        shadowMapSize: 1024,
        maxFPS: 30,
        enableShadows: false,
        enableAntialias: false,
        textureQuality: 'low',
        animationQuality: 'low',
        staticFPS: 10,
        dpr: [0.5, 1]
      };
    }

    // Tier 2: Mid-range devices
    if (tier === 2) {
      return {
        shadowMapSize: 2048,
        maxFPS: 45,
        enableShadows: true,
        enableAntialias: true,
        textureQuality: 'medium',
        animationQuality: 'medium',
        staticFPS: 15,
        dpr: [0.75, 1.5]
      };
    }

    // Tier 3: High-end devices
    return {
      shadowMapSize: 4096,
      maxFPS: 60,
      enableShadows: true,
      enableAntialias: true,
      textureQuality: 'high',
      animationQuality: 'high',
      staticFPS: 20,
      dpr: [1, 2]
    };
  }, [gpuTier]);

  return {
    gpuTier,
    isLoading,
    error,
    performanceSettings
  };
};
